import { api } from './api';

export interface ConnectedMailbox {
  id: string;
  provider: 'gmail' | 'google_workspace' | 'microsoft_365' | 'exchange' | 'imap';
  emailAddress: string;
  displayName: string;
  status: 'active' | 'disconnected' | 'error' | 'revoked';
  lastSyncAt: string | null;
  lastSyncError: string | null;
  totalEmails: number;
  unreadCount: number;
  connectedAt: string;
}

export interface FolderCountEntry {
  total: number;
  unread: number;
}

export interface FolderCounts {
  inbox: FolderCountEntry;
  starred: FolderCountEntry;
  sent: FolderCountEntry;
  drafts: FolderCountEntry;
  scheduled: FolderCountEntry;
  allMail: FolderCountEntry;
  spam: FolderCountEntry;
  trash: FolderCountEntry;
  archive: FolderCountEntry;
  followUps: FolderCountEntry;
  awaitingResponse: FolderCountEntry;
  billingOpportunities: FolderCountEntry;
  attachments: FolderCountEntry;
  matterLinked: FolderCountEntry;
  unlinked: FolderCountEntry;
  suggested: FolderCountEntry;
}

export type MailboxHealthStatus =
  | 'healthy' | 'delayed' | 'stale' | 'error' | 'disconnected' | 'reconnecting';

export type SyncErrorKind = 'auth_required' | 'recovering' | 'transient';

export type MailboxState =
  | 'disabled' | 'disconnected' | 'connecting' | 'initial_sync'
  | 'healthy' | 'delayed' | 'recovering' | 'auth_required';

export interface MailboxHealth {
  mailboxId:         string;
  emailAddress:      string;
  provider:          string;
  healthStatus:      MailboxHealthStatus;
  lastSyncAt:        string | null;
  lastSyncError:     string | null;
  minutesSinceSync:  number | null;
  pendingCount:      number;
  errorCount:        number;
  // Human-facing classification from the backend.
  errorKind:         SyncErrorKind | null;
  needsUserAction:   boolean;
  userTitle:         string;
  userDetail:        string;
  state:             MailboxState;
  retryAt:           string | null;
  lastSuccessfulSync: string | null;
}

export async function fetchMailboxHealth(): Promise<MailboxHealth[]> {
  const res = await api.get('/mailboxes/health');
  return res.data.health;
}

export async function fetchMailboxes(): Promise<ConnectedMailbox[]> {
  const res = await api.get('/mailboxes');
  return res.data.mailboxes;
}

export async function fetchFolderCounts(): Promise<FolderCounts> {
  const res = await api.get('/mailboxes/folder-counts');
  return res.data;
}

export async function disconnectMailbox(mailboxId: string): Promise<void> {
  await api.delete(`/mailboxes/${mailboxId}`);
}

// ─── Historical email hydration (search-readiness) ─────────────

export interface HydrationStatus {
  status: 'none' | 'pending' | 'running' | 'paused' | 'completed' | 'failed';
  total: number;
  hydrated: number;
  remaining: number;
  percent: number; // 0–100
}

export async function fetchHydration(mailboxId: string): Promise<HydrationStatus> {
  const res = await api.get(`/mailboxes/${mailboxId}/hydration`);
  return res.data.hydration;
}

export async function pauseHydration(mailboxId: string): Promise<void> {
  await api.post(`/mailboxes/${mailboxId}/hydration/pause`);
}

export async function resumeHydration(mailboxId: string): Promise<void> {
  await api.post(`/mailboxes/${mailboxId}/hydration/resume`);
}

export async function restartHydration(mailboxId: string): Promise<void> {
  await api.post(`/mailboxes/${mailboxId}/hydration/restart`);
}

/** Where the provider sends the browser back to. Mirrors the server allowlist (oauth.service.ts). */
export type OAuthReturnTo = 'inbox' | 'calendar' | 'email-settings' | 'email-health';

// Calls the authenticated begin endpoint and returns the provider's redirect URL.
// The frontend then does window.location.href = url so the Bearer token is sent via Axios.
//
// `mailboxId` makes this a RECONNECT: the server seals it into the signed state and refuses a
// provider account that is not that mailbox. Without it every "Reconnect" button silently started a
// brand-new connection. `returnTo` brings the user back to the page they started from.
export async function beginOAuthConnect(
  provider: 'google' | 'microsoft',
  opts: { mailboxId?: string; returnTo?: OAuthReturnTo } = {},
): Promise<string> {
  const params: Record<string, string> = {};
  if (opts.mailboxId) params.mailboxId = opts.mailboxId;
  if (opts.returnTo) params.returnTo = opts.returnTo;
  const res = await api.get<{ url: string }>(`/oauth/${provider}/begin`, { params });
  return res.data.url;
}

/** The OAuth provider a stored mailbox reconnects through, or null when it has none (IMAP). */
export function oauthProviderForMailbox(provider: string): 'google' | 'microsoft' | null {
  if (provider === 'gmail' || provider === 'google_workspace') return 'google';
  if (provider === 'microsoft_365' || provider === 'exchange') return 'microsoft';
  return null;
}
